import { useEffect, useState, useRef } from "react"
import { Flex, toast } from "@stardust-ds/react"
import intl from "react-intl-universal"
import Button from "../../../../atoms/Button"

import styles from "./index.module.scss"
import ModalComponent from "../ModalComponent"
import Table from "../../../../atoms/Table"
import useDataApi from "../../../../../hooks/useDataApi"
import {
  verifyIncompleteRuns,
  incompleteRunsList,
  completeRuns,
} from "../../../../../services/ciq"
import RcmpLoading from "../../../../atoms/Loading"
import InfiniteScroll from "../../../../atoms/InfiniteScroll"

export const RcmpIncompleteRunsAlert = () => {
  const [bIsModalOpen, setBIsModalOpen] = useState(false)

  const [oVerifyIncompleteRunsRes, fnVerifyIncompleteRunsReq] =
    useDataApi(verifyIncompleteRuns)
  const [oRegisterRunsRes, fnRegisterRunsReq, fnRegisterRunsReset] =
    useDataApi(completeRuns)

  const bHasIncompleteRuns = Boolean(oVerifyIncompleteRunsRes.data)

  const intervalRef = useRef(null)

  const handleStopVerifyIncompleteRuns = () => {
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }

  useEffect(() => {
    if (bHasIncompleteRuns) {
      handleStopVerifyIncompleteRuns()
      return
    }

    ;(async () => {
      if (oVerifyIncompleteRunsRes.loading) return
      const bResponse = await fnVerifyIncompleteRunsReq()
      if (bResponse) {
        handleStopVerifyIncompleteRuns()
        return
      }

      intervalRef.current = setInterval(async () => {
        if (oVerifyIncompleteRunsRes.loading) return
        const bResponse = await fnVerifyIncompleteRunsReq()
        if (bResponse) handleStopVerifyIncompleteRuns()
      }, 60000)
    })()

    return () => {
      handleStopVerifyIncompleteRuns()
    }
  }, [])

  useEffect(() => {
    if (oRegisterRunsRes.loading || !oRegisterRunsRes.done) return

    if (oRegisterRunsRes.data) {
      toast({
        type: "success",
        description: intl.get("ciq.incompletedRunsAlert.successRegisterRun"),
      })
      fnCloseModal()
      fnRegisterRunsReset()
      fnVerifyIncompleteRunsReq()
      return
    }

    if (oRegisterRunsRes.error) {
      toast({
        type: "error",
        description: intl.get("ciq.incompletedRunsAlert.errorRegisterRun"),
      })
    }
  }, [oRegisterRunsRes])

  const fnOpenModal = () => {
    setBIsModalOpen(true)
  }

  const fnCloseModal = () => {
    if (oRegisterRunsRes.loading) return

    setBIsModalOpen(false)
  }

  const fnRegisterRuns = async () => {
    await fnRegisterRunsReq()
  }

  if (bHasIncompleteRuns !== true) return null

  return (
    <>
      <Flex
        width="100%"
        bColor="#FF6B35"
        bWidth="1px"
        bRadius="8px"
        alignContent="center"
        justifyContent="space-between"
        className={styles.incompleteRunsAlertContainer}
      >
        <p>{intl.get("ciq.incompletedRunsAlert.alertText")}</p>
        <Button onClick={fnOpenModal}>
          {intl.get("ciq.incompletedRunsAlert.alertButton")}
        </Button>
      </Flex>
      {bIsModalOpen && (
        <ModalComponent
          title={intl.get("ciq.incompletedRunsAlert.alertModalTitle")}
          fnCloseModal={fnCloseModal}
          size="lg"
          className={styles.incompleteRunsTableModal}
        >
          <div className={styles.incompleteRunsTableContainer}>
            <RcmpIncompleteRunsList
              fnCloseModal={fnCloseModal}
              oRegisterRunsRes={oRegisterRunsRes}
              fnRegisterRuns={fnRegisterRuns}
            />
          </div>
        </ModalComponent>
      )}
    </>
  )
}

const RcmpIncompleteRunsList = ({ fnCloseModal, oRegisterRunsRes, fnRegisterRuns }) => {
  const [oIncompleteRunsListRes, fnOIncompleteRunsListReq, fnOIncompleteRunsListReset] =
    useDataApi(incompleteRunsList)

  const [nPage, setNPage] = useState(1)
  const [aIncompleteRuns, fnSetAIncompleteRuns] = useState([])

  useEffect(() => {
    if (oIncompleteRunsListRes.loading) return
    ;(async () => {
      if (nPage <= 1) fnSetAIncompleteRuns([])
      const response = await fnOIncompleteRunsListReq(nPage)
      if (response) {
        fnSetAIncompleteRuns((prev) => [...prev, ...(response?.data?.data || [])])
      }
    })()

    return () => {
      fnOIncompleteRunsListReset()
    }
  }, [nPage])

  useEffect(() => {
    if (oIncompleteRunsListRes.loading) return

    if (oIncompleteRunsListRes.error) {
      toast({
        type: "error",
        description: intl.get("ciq.incompletedRunsAlert.errorListIncompleteRuns"),
      })
    }
  }, [oIncompleteRunsListRes])

  if (oIncompleteRunsListRes?.loading && !aIncompleteRuns.length)
    return <RcmpLoading bShow={true} />

  if (!aIncompleteRuns.length) {
    return (
      <p className={styles.noIncompleteRuns}>
        {intl.get("ciq.incompletedRunsAlert.noIncompleteRuns")}
      </p>
    )
  }

  const fnNextPage = (page) => {
    setNPage(page)
  }

  return (
    <InfiniteScroll
      fnNext={fnNextPage}
      nTotalPages={oIncompleteRunsListRes?.data?.data?.last_page}
      nDataLength={aIncompleteRuns.length}
      bStopPagination={oIncompleteRunsListRes.loading}
      scrollableTarget="modal-overflow-content"
    >
      <Table bHideArrow={true}>
        <Table.THead>
          <Table.HeadData>
            {intl.get("ciq.incompletedRunsAlert.equipment")}
          </Table.HeadData>
          <Table.HeadData>{intl.get("ciq.incompletedRunsAlert.analyte")}</Table.HeadData>
        </Table.THead>
        <Table.TBody>
          {aIncompleteRuns?.map((run, i) => (
            <Table.TRow key={`incomplete-run-${i}`}>
              <Table.TData>{run?.equipment_cname}</Table.TData>
              <Table.TData>{run?.anlt_cname}</Table.TData>
            </Table.TRow>
          ))}
        </Table.TBody>
      </Table>

      <RcmpLoading
        bShow={oIncompleteRunsListRes.loading}
        className={styles.loadingIncompleteRuns}
      />

      <Flex width="100%" alignContent="center" justifyContent="space-between" mTop="xxs">
        <Button
          onClick={fnCloseModal}
          variant="outline"
          disabled={oRegisterRunsRes.loading}
          className={styles.cancelButton}
        >
          {intl.get("cancel")}
        </Button>
        <Button
          onClick={fnRegisterRuns}
          isLoading={oRegisterRunsRes.loading}
          className={styles.saveButton}
        >
          {intl.get("ciq.incompletedRunsAlert.modalRegisterRunButton")}
        </Button>
      </Flex>
    </InfiniteScroll>
  )
}
